import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SectionHeader } from '../components/shared/SectionHeader';
import { FeatureCard } from '../components/shared/FeatureCard';
import { TestimonialCard } from '../components/shared/TestimonialCard';
import { CTAStrip } from '../components/shared/CTAStrip';
import { FAQ } from '../components/shared/FAQ';
import { 
  ArrowRight, 
  Shield, 
  FileCheck, 
  Clock, 
  Eye, 
  BarChart3,
  FileText,
  ClipboardCheck,
  Globe,
  Upload,
  Bell,
  CreditCard,
  CheckCircle2,
  XCircle,
  Users,
  Building2,
  Truck,
  Monitor
} from 'lucide-react';

// Images
const HERO_BG = "https://images.pexels.com/photos/16070292/pexels-photo-16070292.jpeg";

// Product Screenshots for Carousel
const productScreenshots = [
  {
    url: "https://customer-assets.emergentagent.com/job_4b0cd8b7-dd81-4c3b-b959-0027fd70ebf1/artifacts/ytr8lv7e_product_overview.png",
    title: "System Dashboard",
    description: "Complete overview of your certification operations"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_4b0cd8b7-dd81-4c3b-b959-0027fd70ebf1/artifacts/am7k6jvl_CertificationHub.png",
    title: "Certification Hub",
    description: "Manage all certificates in one place"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_4b0cd8b7-dd81-4c3b-b959-0027fd70ebf1/artifacts/g3itfo5j_market_analysis.png",
    title: "Market Analysis",
    description: "Insights and analytics at your fingertips"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_4b0cd8b7-dd81-4c3b-b959-0027fd70ebf1/artifacts/sarl35gw_archives.png",
    title: "Document Archives",
    description: "Secure storage for all your certification documents"
  }
];

const trustBadges = [
  { icon: Shield, text: "Audit Trails" },
  { icon: Users, text: "Role-based Access" },
  { icon: FileCheck, text: "Standards-aligned" },
  { icon: Globe, text: "Cloud-ready" },
  { icon: CheckCircle2, text: "Traceable Approvals" }
];

const problems = [
  "Manual paperwork causing delays",
  "Inconsistent certificate templates",
  "Slow approval processes",
  "Lost or misplaced documents",
  "Weak traceability and audit gaps",
  "Limited visibility and reporting"
];

const solutions = [
  "End-to-end digital workflows",
  "Configurable rules engine",
  "Secure document generation",
  "Centralized record management",
  "Faster turnaround times",
  "Real-time reporting dashboards"
];

const workflowSteps = [
  {
    number: "01",
    title: "Apply",
    description: "Exporter submits application with required documents through the portal"
  },
  {
    number: "02",
    title: "Validate",
    description: "System verifies commodity and country-specific export requirements"
  },
  {
    number: "03",
    title: "Inspect",
    description: "Inspectors assigned based on office location and availability"
  },
  {
    number: "04",
    title: "Approve",
    description: "Digital review with notes, corrections, and final sign-off"
  },
  {
    number: "05",
    title: "Issue",
    description: "Official certificate PDF with barcode, QR code, and watermark"
  }
];

const outcomes = [
  { metric: "60%", label: "Faster Turnaround", description: "Average processing time reduction" },
  { metric: "85%", label: "Reduced Errors", description: "Through automated validation" },
  { metric: "100%", label: "Compliance", description: "With international standards" },
  { metric: "Real-time", label: "Transparency", description: "Track every application" }
];

const modules = [
  { icon: FileText, title: "Certificate Requests", description: "Streamlined application intake and processing" },
  { icon: ClipboardCheck, title: "Inspection Management", description: "Queue-based inspector assignment and workflows" },
  { icon: Globe, title: "Export Requirements", description: "Commodity and country-specific rules engine" },
  { icon: Upload, title: "Document Management", description: "Secure storage for PDF, JPG, PNG, and Excel files" },
  { icon: Bell, title: "Notifications", description: "Email alerts and event-driven messaging" },
  { icon: CreditCard, title: "Billing", description: "Service-based billing with credit management" }
];

const testimonials = [
  {
    quote: "Certisfy has transformed how we handle export documentation. What used to take days now takes hours.",
    author: "Sarah Mokoena",
    role: "Export Manager",
    organization: "Fresh Produce SA"
  },
  {
    quote: "The inspection queue system has streamlined our operations significantly. We can now handle twice the volume with the same team.",
    author: "Dr. James Ndlovu",
    role: "Chief Inspector",
    organization: "NPPO"
  },
  {
    quote: "Finally, a system that gives us real visibility into certificate issuance across all regions.",
    author: "Minister Thandi Dlamini",
    role: "Director of Trade",
    organization: "Ministry of Agriculture"
  }
];

// Product Showcase Carousel Component
const ProductShowcase = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % productScreenshots.length);
        setIsTransitioning(false);
      }, 500);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section data-testid="product-showcase" className="py-12 lg:py-16 bg-slate-900">
      <div className="container-custom">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white text-sm mb-4">
            <Monitor className="w-4 h-4 text-[#C8A24A]" />
            See the Platform
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3">
            Powerful tools, intuitive design
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Experience a modern interface designed for efficiency and clarity
          </p>
        </div>

        {/* Screenshot Display */}
        <div className="relative max-w-5xl mx-auto">
          {/* Browser Frame */}
          <div className="bg-slate-800 rounded-t-xl p-3 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500/70" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
              <div className="w-3 h-3 rounded-full bg-green-500/70" />
            </div>
            <div className="flex-1 ml-4">
              <div className="bg-slate-700 rounded-md px-4 py-1.5 text-xs text-slate-400 max-w-md">
                app.certisfy.com/{productScreenshots[currentIndex].title.toLowerCase().replace(/\s+/g, '-')}
              </div>
            </div>
          </div>

          {/* Screenshot Container */}
          <div className="relative bg-slate-800 rounded-b-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
            {productScreenshots.map((screenshot, index) => (
              <img
                key={index}
                src={screenshot.url}
                alt={screenshot.title}
                className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${
                  index === currentIndex && !isTransitioning ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
            
            {/* Gradient overlay at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-900/80 to-transparent" />
          </div>

          {/* Caption */}
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
            <div className="text-white">
              <h3 className="font-bold text-lg">{productScreenshots[currentIndex].title}</h3>
              <p className="text-slate-300 text-sm">{productScreenshots[currentIndex].description}</p>
            </div>
          </div>
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center gap-2 mt-6">
          {productScreenshots.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsTransitioning(true);
                setTimeout(() => {
                  setCurrentIndex(index);
                  setIsTransitioning(false);
                }, 300);
              }}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                index === currentIndex 
                  ? 'bg-[#C8A24A] w-8' 
                  : 'bg-slate-600 hover:bg-slate-500'
              }`}
              aria-label={`View ${productScreenshots[index].title}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-[90vh] flex items-center overflow-hidden"
      >
        {/* Background Image with Pan Animation */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="hero-pan-container absolute inset-0">
            <img 
              src={HERO_BG} 
              alt="Agricultural fields in Southern Africa"
              className="hero-pan-image w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 hero-overlay" />
        </div>

        {/* Content */}
        <div className="container-custom relative z-10 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm mb-6 animate-fade-in">
              <Shield className="w-4 h-4 text-[#C8A24A]" />
              National-grade certification platform
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-slide-up">
              Phytosanitary certification and export compliance, 
              <span className="text-[#C8A24A]"> simplified.</span>
            </h1>
            
            <p className="text-xl text-slate-200 mb-8 leading-relaxed max-w-2xl animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Certisfy streamlines certificate applications, inspections, approvals, 
              and document generation across the entire export journey. Built for Southern Africa's 
              agricultural trade ecosystem.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact?demo=true" data-testid="hero-cta-demo">
                <Button className="bg-white text-[#1F7A3A] hover:bg-slate-100 rounded-full px-8 py-3 font-semibold shadow-lg hover:shadow-xl inline-flex items-center gap-2">
                  Request a Demo
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/product" data-testid="hero-cta-product">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-semibold">
                  View Product
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/60 rounded-full" />
          </div>
        </div>
      </section>

      {/* Trust Band */}
      <section data-testid="trust-band" className="py-8 bg-white border-b border-slate-200">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-slate-600 font-medium text-center md:text-left">
              Built for national systems, exporters, and inspection teams
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {trustBadges.map((badge, index) => (
                <div 
                  key={index}
                  className="trust-badge"
                  data-testid={`trust-badge-${index}`}
                >
                  <badge.icon className="w-4 h-4 text-[#1F7A3A]" />
                  {badge.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problem → Solution */}
      <section data-testid="problem-solution" className="py-12 lg:py-16 green-gradient-bg">
        <div className="container-custom">
          <SectionHeader 
            badge="Why Certisfy?"
            title="From paperwork chaos to streamlined compliance"
            subtitle="Traditional certification processes are slow, error-prone, and lack visibility. Certisfy changes that."
          />

          <div className="grid md:grid-cols-2 gap-8 mt-10">
            {/* Problems */}
            <div className="bg-white rounded-2xl p-8 shadow-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">The Challenge</h3>
              </div>
              <ul className="space-y-4">
                {problems.map((problem, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-600">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    {problem}
                  </li>
                ))}
              </ul>
            </div>

            {/* Solutions */}
            <div className="bg-[#1F7A3A] rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-[#C8A24A]" />
                </div>
                <h3 className="text-xl font-bold">The Certisfy Solution</h3>
              </div>
              <ul className="space-y-4">
                {solutions.map((solution, index) => (
                  <li key={index} className="flex items-start gap-3 text-green-100">
                    <CheckCircle2 className="w-5 h-5 text-[#C8A24A] mt-0.5 flex-shrink-0" />
                    {solution}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase Carousel */}
      <ProductShowcase />

      {/* How It Works */}
      <section data-testid="how-it-works" className="py-12 lg:py-16 bg-white">
        <div className="container-custom">
          <SectionHeader 
            badge="Process"
            title="How Certisfy Works"
            subtitle="A simple 5-step workflow from application to certificate issuance"
          />

          <div className="mt-10">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {workflowSteps.map((step, index) => (
                <div 
                  key={index}
                  className="relative text-center"
                  data-testid={`workflow-step-${index + 1}`}
                >
                  <div className="step-number mx-auto mb-4">{step.number}</div>
                  <h4 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h4>
                  <p className="text-sm text-slate-600">{step.description}</p>
                  
                  {/* Connector line */}
                  {index < workflowSteps.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-[calc(50%+24px)] w-[calc(100%-48px)] h-0.5 bg-slate-200" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Outcomes */}
      <section data-testid="outcomes" className="py-12 lg:py-16 bg-slate-50">
        <div className="container-custom">
          <SectionHeader 
            badge="Results"
            title="Measurable Impact"
            subtitle="Real outcomes from organizations using Certisfy"
          />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
            {outcomes.map((outcome, index) => (
              <div 
                key={index}
                className="card-base text-center"
                data-testid={`outcome-${index}`}
              >
                <p className="text-4xl lg:text-5xl font-bold text-[#1F7A3A] mb-2">
                  {outcome.metric}
                </p>
                <p className="text-lg font-semibold text-slate-900 mb-1">{outcome.label}</p>
                <p className="text-sm text-slate-500">{outcome.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Modules */}
      <section data-testid="modules-teaser" className="py-12 lg:py-16 bg-white">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-end justify-between gap-6 mb-12">
            <SectionHeader 
              badge="Platform"
              title="Core Modules"
              subtitle="A comprehensive suite of tools for every stakeholder in the certification process"
              centered={false}
            />
            <Link 
              to="/modules" 
              className="flex items-center gap-2 text-[#1F7A3A] font-medium hover:text-[#145A2A]"
              data-testid="view-all-modules"
            >
              View all modules
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {modules.map((module, index) => (
              <FeatureCard
                key={index}
                icon={module.icon}
                title={module.title}
                description={module.description}
                link="/modules"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials" className="py-12 lg:py-16 bg-slate-50">
        <div className="container-custom">
          <SectionHeader 
            badge="Testimonials"
            title="Trusted by Industry Leaders"
            subtitle="Hear from the exporters, inspectors, and regulators who use Certisfy"
          />

          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                quote={testimonial.quote}
                author={testimonial.author}
                role={testimonial.role}
                organization={testimonial.organization}
                variant={index === 1 ? "featured" : "default"}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* CTA Strip */}
      <CTAStrip />
    </div>
  );
}
